import {AfterContentInit, Component, ContentChildren, forwardRef, Injector, Optional, QueryList} from "@angular/core";
import {ErrorHandlerService} from "../../../../../error-handler.service";
import {DataClientError, EntityModel, SearchModel} from "@fedeltapointofsale/data-client";
import {SearchAction, SearchActionSelector} from "../actions/search-action.component";
import {ColumnComponent} from "../../../grid/column.component";
import {SearchActionEvent} from "../../search-page/search-action-event";
import {FUIStackedPageContextService} from "@fedeltapointofsale/ui";
import {AbstractColumnComponent} from "../../../grid/abstract-column.component";
import {SearchType} from "../../search-page/extended-search-params";
import {Router} from "@angular/router";
import {EditPageComponent} from "../../../edit/edit-page/edit-page.component";

export class CloneSearchAction implements SearchAction {
    readonly CLONE_NAME: string = "wbo.common.button.clone";
    readonly CLONE_ICON: string = "clone";

    readonly IMPORTANCE: number = 40;

    private available: boolean = false;

    constructor(private searchModel: SearchModel<any>, private entityModel: EntityModel<any>, private router: Router) {
        this.entityModel.canCreateEntity().then((canCreate: boolean) => this.available = canCreate && this.entityModel.isEntityCloneable());
    }

    getImportance(): number {
        return this.IMPORTANCE;
    }

    getIcon(data: any): string {
        return this.CLONE_ICON;
    }

    getIconClass(data: any): string {
        return "";
    }

    getIconSize(data: any): number {
        return 17;
    }

    getName(data: any): string {
        return this.CLONE_NAME;
    }

    doAction(data: any): Promise<void> {
        this.router.navigate(["main", this.searchModel.getResource(), EditPageComponent.NEW_ENTITY_PARAM],
            {queryParams: {clone: this.searchModel.getIdentifier(data)}});
        return null;
    }

    isAvailable(data: any): boolean {
        return this.available;
    }
}

/**
 * Search grid column to add a list of action buttons.
 *
 * The actions are defined as child search-action elements. Search Actions must provide the SearchActionSelector
 * directive and implement the SearchAction interface.
 *
 * Meta data (iconName, name) from the SearchAction components are taken to render the buttons. Click events are
 * delegated to the onClick method implemented on the SearchAction implementations.
 */
@Component({
    selector: "wbo-actions-search-column",
    templateUrl: "./actions-search-column.component.html",
    styleUrls: ["./actions-search-column.component.scss"],
    providers: [
        {provide: ColumnComponent, useExisting: forwardRef(() => ActionsSearchColumnComponent), multi: true}
    ]
})
export class ActionsSearchColumnComponent extends AbstractColumnComponent implements AfterContentInit {

    /**
     * Actions defined for the column.
     *
     */
    @ContentChildren(SearchActionSelector) actionChildren: QueryList<SearchAction>;

    /**
     * List of Search Actions ordered by importance
     */
    actions: SearchAction[] = [];

    override columnName: string = "action";

    constructor(injector: Injector,
                private errorHandlerService: ErrorHandlerService,
                @Optional() private stackedPageContextService: FUIStackedPageContextService,
                @Optional() protected entityModel: EntityModel<any>,
                private router: Router) {
        super(injector);
    }

    ngAfterContentInit(): void {
        //
        // Arrange the actions by their importance
        //
        this.actions = this.actionChildren.toArray();

        if (this.entityModel && this.entityModel.isEntityCloneable()) {
            this.actions.push(new CloneSearchAction(this.searchModel, this.entityModel, this.router));
        }

        this.actions.sort((a1: SearchAction, a2: SearchAction) => {
            return a2.getImportance() - a1.getImportance();
        });
    }

    /**
     * Invoked when the action button is clicked. Delegate to the underlying action components
     * @param action Action to delegate to
     * @param data Grid row data
     */
    doAction(action: SearchAction, data: any): void {
        this.itemAction.emit(new SearchActionEvent(SearchActionEvent.ACTION_START, data));

        const promiseAction: Promise<void> = action.doAction(data);
        if (promiseAction != null) {
            promiseAction.then(() => {
                this.itemAction.emit(new SearchActionEvent(SearchActionEvent.ACTION_END, data))
            }).catch((error: DataClientError) => {
                this.itemAction.emit(new SearchActionEvent(SearchActionEvent.ACTION_END, data));
                this.errorHandlerService.displayDataClientError(error);
            });
        }
    }

    override isVisible(): boolean {
        // only show this column when not in select mode
        return super.isVisible() && (!this.stackedPageContextService || this.stackedPageContextService.params.searchType === SearchType.NO_SELECTION);
    }

    emitItemAction(action: SearchActionEvent): void {
        this.itemAction.emit(action);
    }
}
